/**
 * Parâmetros e auditoria (DEMO-03, telas/Administracao.tsx › ParametrosAuditoria),
 * persistente: abatimento da comissão líquida estimada (vigência, histórico,
 * usuário, motivo) e a trilha das ações sensíveis do Comercial (lida da
 * auditoria). A nova vigência é ação sensível 🔒 (permissão de alterar
 * parâmetros) e nunca recalcula documentos históricos.
 */
import { LinhaPersonalizacao, usePersonalizacao } from './personalizacao.tsx';
import { useState } from 'react';
import { api } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { dataHora } from '../formatos.ts';
import { isoParaBr } from '../../../backend/src/compartilhado/documentos.ts';
import { Ajuda, Estado, EstadoCarga, Modal, Nota, ResumoIndicadores, useCarga } from './ui.tsx';
import { CampoData } from './CampoData.tsx';
import { CampoCnpj } from './campos.tsx';
import { useAmbiente } from '../ambiente.tsx';
import { pctNum } from './Parceiros.tsx';

interface Vigencia {
  id: string;
  percentual: number;
  vigente_de: string;
  vigente_ate: string | null;
  registrado_em: string;
  registrado_por: string;
  motivo: string;
}
interface Abatimento {
  hoje: string;
  vigente: Vigencia | null;
  historico: Vigencia[];
}
interface AcaoSensivel {
  data_hora: string;
  usuario: string;
  acao: string;
  objeto: string;
  detalhe: string;
  justificativa: string | null;
}

export function ParametrosAuditoria() {
  const { pode } = useSessao();
  const [nova, setNova] = useState(false);
  const carga = useCarga(() => api.get<Abatimento>('/api/comercial/abatimento'), []);
  const pers = usePersonalizacao('parametros-auditoria'); // D7: colunas e visões da trilha
  const trilha = useCarga(() => (pode('visualizar', 'log_auditoria') ? api.get<AcaoSensivel[]>('/api/comercial/acoes-sensiveis') : Promise.resolve(null)), []);
  const a = carga.dados;
  return (
    <>
      <h1 className="titulo-pagina">Parâmetros e auditoria</h1>
      <EstadoCarga carregando={carga.carregando && !a} erro={carga.erro} recarregar={carga.recarregar} teste="abatimento" />
      {a && (
        <section className="resumo-destaque" data-teste="parametro-abatimento">
          <div className="barra-titulo">
            <h2>
              Abatimento da comissão líquida estimada
              <Ajuda titulo="Comissão líquida estimada">Estimativa INTERNA: comissão bruta × (1 − abatimento). Com 22,5%: bruta × 77,5%. Não é alíquota de tributo e não aparece em documentos enviados ao Cliente ou ao parceiro.</Ajuda>
            </h2>
            {pode('editar', 'parametro') && (
              <div className="acoes-direita">
                <button type="button" className="botao-secundario" onClick={() => setNova(true)} data-teste="nova-vigencia">
                  Registrar nova vigência 🔒
                </button>
              </div>
            )}
          </div>
          {a.vigente ? (
            <ResumoIndicadores
              itens={[
                { rotulo: 'Percentual vigente', valor: <span data-teste="abatimento-vigente">{pctNum(a.vigente.percentual)}</span> },
                { rotulo: 'Fator sobre a comissão bruta', valor: pctNum(100 - a.vigente.percentual) },
                { rotulo: 'Vigente desde', valor: isoParaBr(a.vigente.vigente_de) },
                { rotulo: 'Registrado por', valor: a.vigente.registrado_por },
              ]}
            />
          ) : (
            <Estado tipo="vazio" titulo="nenhuma vigência cadastrada: registre a vigência aprovada pela Inttrix" teste="abatimento-sem-vigencia" />
          )}
          <p className="texto-secundario sem-margem">Exemplo: comissão bruta de R$ 2.700,00 → líquida estimada de R$ 2.092,50 (77,5%). O percentual de comissão da OPERAÇÃO (ex.: 20% no Trade-in) é outra coisa.</p>
        </section>
      )}
      {a && (
        <>
          <h3>Histórico de vigências</h3>
          <div className="rolagem">
            <table className="tabela tabela-compacta" data-teste="historico-abatimento">
              <thead>
                <tr>
                  <th>Percentual</th>
                  <th>Vigente desde</th>
                  <th>Vigente até</th>
                  <th>Registrado por</th>
                  <th>Registrado em</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {a.historico.map((h) => (
                  <tr key={h.id}>
                    <td>{pctNum(h.percentual)}</td>
                    <td>{isoParaBr(h.vigente_de)}</td>
                    <td>{h.vigente_ate ? isoParaBr(h.vigente_ate) : '—'}</td>
                    <td>{h.registrado_por}</td>
                    <td>{dataHora(h.registrado_em)}</td>
                    <td>{h.motivo || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <Nota>Pedidos e versões enviadas guardam o percentual aplicado no momento: uma nova vigência nunca recalcula documentos históricos.</Nota>

      <DadosEmpresa aoMudar={() => void trilha.recarregar()} />

      <h2>Auditoria das ações sensíveis</h2>
      <p className="texto-secundario">Alterar comissão, registrar vigência, inativar ou reativar cadastro, importar produtos e editar entidade legal (e, nas próximas etapas, PTAX, valores internos, conta, documentos e dado congelado): sempre com usuário, data e hora, detalhe e justificativa quando exigida. A trilha completa fica em Administração › Auditoria.</p>
      {!pode('visualizar', 'log_auditoria') ? (
        <Estado tipo="bloqueado" titulo="a trilha exige a permissão de ver o log de auditoria" />
      ) : (
        <>
          <EstadoCarga carregando={trilha.carregando && !trilha.dados} erro={trilha.erro} recarregar={trilha.recarregar} teste="acoes-sensiveis" />
          {trilha.dados &&
            (trilha.dados.length === 0 ? (
              <Estado tipo="vazio" titulo="nenhuma ação sensível registrada" />
            ) : (
              <>
              <LinhaPersonalizacao>{pers.controles}</LinhaPersonalizacao>
              <div className="rolagem">
                <table ref={pers.tabela} className="tabela tabela-compacta" data-teste="auditoria">
                  <thead>
                    <tr>
                      <th>Data e hora</th>
                      <th>Usuário</th>
                      <th>Ação</th>
                      <th>Objeto</th>
                      <th>Detalhe</th>
                      <th>Justificativa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trilha.dados.map((x, i) => (
                      <tr key={i}>
                        <td className="nowrap">{dataHora(x.data_hora)}</td>
                        <td>{x.usuario}</td>
                        <td>{x.acao}</td>
                        <td>{x.objeto}</td>
                        <td>{x.detalhe}</td>
                        <td>{x.justificativa ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            ))}
        </>
      )}
      {nova && a && <ModalVigencia hoje={a.hoje} aoFechar={() => setNova(false)} aoSalvar={() => (setNova(false), void carga.recarregar(), void trilha.recarregar())} />}
    </>
  );
}

function ModalVigencia({ hoje, aoFechar, aoSalvar }: { hoje: string; aoFechar: () => void; aoSalvar: () => void }) {
  const [pct, setPct] = useState('');
  const [desde, setDesde] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const ok = Number(pct) >= 0 && Number(pct) < 100 && pct !== '' && desde >= hoje && motivo.trim().length > 3;
  const salvar = async () => {
    setErro('');
    try {
      await api.post('/api/comercial/abatimento', { percentual: Number(pct), vigente_de: desde, motivo });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo="Registrar nova vigência do abatimento"
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={!ok} onClick={salvar} data-teste="salvar-vigencia">
            Registrar vigência
          </button>
        </>
      }
    >
      <p className="aviso aviso-info">Ação sensível (permissão: alterar parâmetros). Vale só a partir da data informada; documentos já emitidos mantêm o percentual deles.</p>
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-vigencia">
          {erro}
        </p>
      )}
      <label className="campo">
        Percentual de abatimento (%)
        <input type="number" min={0} max={99} step="0.1" value={pct} onChange={(e) => setPct(e.target.value)} data-teste="vigencia-percentual" />
      </label>
      <CampoData rotulo="Vigente a partir de" valor={desde} aoMudar={setDesde} min={hoje} teste="vigencia-data" obrigatorio />
      <label className="campo">
        Motivo (obrigatório)
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} data-teste="vigencia-motivo" />
      </label>
    </Modal>
  );
}

interface Empresa {
  razao: string;
  cnpj: string;
  endereco?: string;
  telefone?: string;
  email?: string;
  site?: string;
}
interface VigenciaEmpresa {
  id: string;
  dados: Empresa;
  vigente_de: string;
  vigente_ate: string | null;
  registrado_em: string;
  registrado_por: string;
  motivo: string;
}

/**
 * P-18: dados da INTTRIX nos documentos (um emissor: a razão social e o CNPJ que constam das Propostas).
 * Parâmetro com vigência e motivo; a versão enviada guarda o snapshot (documentos antigos não mudam).
 */
function DadosEmpresa({ aoMudar }: { aoMudar: () => void }) {
  const { pode } = useSessao();
  const [editar, setEditar] = useState(false);
  const carga = useCarga(() => api.get<{ hoje: string; vigente: VigenciaEmpresa | null; historico: VigenciaEmpresa[] }>('/api/comercial/empresa'), []);
  const x = carga.dados;
  if (!x) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="empresa" />;
  const e = x.vigente?.dados;
  return (
    <section className="cartao" data-teste="dados-empresa">
      <div className="barra-titulo">
        <h2>
          Dados da Inttrix nos documentos
          <Ajuda titulo="Dados da Inttrix">Razão social, CNPJ, endereço e contatos que aparecem nas Propostas enviadas ao Cliente. Cada versão enviada guarda uma cópia desses dados: mudar aqui não altera documentos já enviados. Sem cadastro, os PDFs saem como prévia.</Ajuda>
        </h2>
        {pode('editar', 'parametro') && (
          <div className="acoes-direita">
            <button type="button" className="botao-secundario" onClick={() => setEditar(true)} data-teste="editar-empresa">
              {e ? 'Registrar nova vigência 🔒' : 'Cadastrar dados da Inttrix 🔒'}
            </button>
          </div>
        )}
      </div>
      {e ? (
        <ResumoIndicadores
          compacto
          itens={[
            { rotulo: 'Razão social', valor: e.razao },
            { rotulo: 'CNPJ', valor: e.cnpj },
            { rotulo: 'Vigente desde', valor: isoParaBr(x.vigente!.vigente_de), detalhe: `por ${x.vigente!.registrado_por}` },
          ]}
          secundarios={[
            { rotulo: 'Endereço', valor: e.endereco || '—' },
            { rotulo: 'Telefone · e-mail · site', valor: [e.telefone, e.email, e.site].filter(Boolean).join(' · ') || '—' },
          ]}
        />
      ) : (
        <Estado tipo="bloqueado" titulo="Dados da Inttrix ainda não cadastrados: os PDFs das Propostas saem como prévia" teste="empresa-pendente" />
      )}
      {x.historico.length > 1 && (
        <ul className="linha-tempo" data-teste="historico-empresa">
          {x.historico.map((h) => (
            <li key={h.id}>
              <strong>{isoParaBr(h.vigente_de)}</strong> · {h.dados.razao} — {h.dados.cnpj} <span className="texto-secundario">({h.registrado_por}: {h.motivo})</span>
            </li>
          ))}
        </ul>
      )}
      {editar && <ModalEmpresa atual={e} hoje={x.hoje} aoFechar={() => setEditar(false)} aoSalvar={() => (setEditar(false), void carga.recarregar(), aoMudar())} />}
    </section>
  );
}

function ModalEmpresa({ atual, hoje, aoFechar, aoSalvar }: { atual?: Empresa; hoje: string; aoFechar: () => void; aoSalvar: () => void }) {
  const { aceitarFicticio } = useAmbiente();
  const [e, setE] = useState<Empresa>(atual ?? { razao: '', cnpj: '' });
  const [cnpjOk, setCnpjOk] = useState(Boolean(atual?.cnpj));
  const [desde, setDesde] = useState(hoje);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const ok = e.razao.trim() && cnpjOk && motivo.trim().length > 3 && desde >= hoje;
  const salvar = async () => {
    try {
      await api.post('/api/comercial/empresa', { ...e, vigente_de: desde, motivo });
      aoSalvar();
    } catch (x) {
      setErro((x as Error).message);
    }
  };
  const campo = (k: keyof Empresa, rotulo: string, largo = false) => (
    <label className={largo ? 'coluna-dupla' : undefined}>
      {rotulo}
      <input value={e[k] ?? ''} onChange={(ev) => setE({ ...e, [k]: ev.target.value })} data-teste={`empresa-${k}`} />
    </label>
  );
  return (
    <Modal
      titulo="Dados da Inttrix nos documentos (ação sensível)"
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={!ok} onClick={() => void salvar()} data-teste="salvar-empresa">
            Registrar
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <div className="grade-form sem-borda">
        {campo('razao', 'Razão social *', true)}
        <CampoCnpj valor={e.cnpj} aoMudar={(v, valido) => (setE({ ...e, cnpj: v }), setCnpjOk(valido))} teste="empresa-cnpj" obrigatorio aceitarFicticio={aceitarFicticio} />
        {campo('endereco', 'Endereço', true)}
        {campo('telefone', 'Telefone')}
        {campo('email', 'E-mail')}
        {campo('site', 'Site')}
        <CampoData rotulo="Vigente a partir de" valor={desde} aoMudar={setDesde} min={hoje} teste="empresa-vigencia" obrigatorio />
        <label className="largura-total">
          Motivo (obrigatório)
          <input value={motivo} onChange={(ev) => setMotivo(ev.target.value)} data-teste="empresa-motivo" />
        </label>
      </div>
      <Nota>Versões já enviadas mantêm os dados da época (cópia na versão). Hoje as Propostas saem por um único CNPJ emissor.</Nota>
    </Modal>
  );
}
